import { useReducer, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { getApi, postApi } from 'redux/apis';
import {
	wizardReducer,
	initialState,
	type WizardState,
} from 'components/EmployeeCost/SmartImport/wizardReducer';
import { loadFile, aggregateByEmployee, isTotalRow } from 'components/EmployeeCost/SmartImport/parsers';
import { buildParsePayload } from 'components/EmployeeCost/SmartImport/buildParsePayload';
import {
	buildAutoDetectedMappings,
	buildMappingsPayload,
	getStep2ValidationError,
	parseTemplateEntries,
	resolveTemplateToMappings,
	suggestCategories,
	type MappingTemplate,
	type SectionDto,
} from 'components/EmployeeCost/SmartImport/steps/step2Helpers';
import {
	buildAggregatedRows,
	computeEmployeeRows,
	type RosterEmployee,
} from 'components/EmployeeCost/SmartImport/steps/step3FuzzyMatch';
import { buildNameColConfig } from 'components/EmployeeCost/SmartImport/steps/Step3MapEmployees';
import {
	buildConfirmPayload,
	getErrorLocus,
	getErrorStep,
	isPendingFieldKey,
	parseConfirmErrors,
	type PreValidationError,
	type SmartImportResponse,
} from 'components/EmployeeCost/SmartImport/steps/step4Confirm';
import {
	autoSelectSoleField,
	detectNegativeDefaults,
	findBestTemplateMatch,
	type TemplateMatch,
} from './chatImportHelpers';
import type { PayPeriodOption, ResultSummary, SmartImportStepKind } from './types';

const FIELDS_URL = '/employee-cost/import/fields';
const TEMPLATES_URL = '/employee-cost/import/mapping-templates';
const EMPLOYEES_URL = '/employees/list';
const ALIASES_URL = '/employee-cost/import/name-aliases';
const CONFIRM_URL = '/employee-cost/import/smart';
const PAY_PERIODS_URL = '/pay-periods';

const isBlankNewName = (r: WizardState['employeeRows'][number]): boolean =>
	r.matchStatus === 'new' && (r.addAsNewName ?? r.csvName).trim() === '';

const formatPeriodLabel = (p: { startDate?: string; endDate?: string }): string =>
	p.startDate && p.endDate
		? `${moment(p.startDate).tz(process.env.REACT_APP_TIMEZONE!).format('MM/DD/YYYY')} - ${moment(p.endDate)
				.tz(process.env.REACT_APP_TIMEZONE!)
				.format('MM/DD/YYYY')}`
		: '';

interface Params {
	companyId: string;
	// Appends a new assistant message. `smartImportStep`, when set, marks it as the
	// single currently-interactive card — the caller is responsible for freezing
	// the previous one first (see freezeActiveMessage).
	appendMessage: (msg: { text: string; smartImportStep?: SmartImportStepKind }) => void;
	// Overwrites the text of the currently-interactive message and clears its
	// smartImportStep, turning it into inert history.
	freezeActiveMessage: (text: string) => void;
	// Called once an import successfully commits, so the caller can notify any
	// open Employee Cost table to refetch (redux/slice/chatBubbleSlice's
	// notifyDataChanged).
	notifyImported: (payPeriodId: string) => void;
}

/**
 * Drives the Smart Import wizard's own reducer/helpers (components/EmployeeCost/
 * SmartImport) as a sequence of chat cards instead of a modal's Steps. No business
 * logic lives here — this is orchestration + the two chat-only additions
 * (negative-amount defaulting, on-upload template suggestion) from
 * chatImportHelpers. Endpoints and payload shapes are identical to the modal
 * wizard's Step 2-4.
 */
export function useSmartImportChat({ companyId, appendMessage, freezeActiveMessage, notifyImported }: Params) {
	const [session, dispatch] = useReducer(wizardReducer, initialState);

	const [sections, setSections] = useState<SectionDto[]>([]);
	const [templates, setTemplates] = useState<MappingTemplate[]>([]);
	const [templateMatch, setTemplateMatch] = useState<TemplateMatch | null>(null);
	const [payPeriodOptions, setPayPeriodOptions] = useState<PayPeriodOption[]>([]);
	const [payPeriodId, setPayPeriodId] = useState<string | null>(null);
	const [payPeriodLabel, setPayPeriodLabel] = useState('');
	const [roster, setRoster] = useState<RosterEmployee[]>([]);
	const [fieldNames, setFieldNames] = useState<Record<string, string>>({});
	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [result, setResult] = useState<ResultSummary | null>(null);

	// The parsed file survives across steps in a ref (not the reducer) — nothing
	// here needs it to trigger a re-render, and it must be read synchronously right
	// after being written, before React has necessarily committed reducer state.
	const parsedRef = useRef<{ headers: string[]; rawMatrix: string[][]; headerRowIndex: number } | null>(null);
	const activeRef = useRef(false);

	const resetLocal = () => {
		setSections([]);
		setTemplates([]);
		setTemplateMatch(null);
		setPayPeriodOptions([]);
		setPayPeriodId(null);
		setPayPeriodLabel('');
		setRoster([]);
		setFieldNames({});
		setLoading(false);
		setSubmitting(false);
		setResult(null);
		parsedRef.current = null;
	};

	// ---- Step: fetch fields/templates for a resolved pay period, then either
	// offer a template or drop straight into the mapping card. -----------------
	const afterPayPeriodResolved = async (resolvedPayPeriodId: string, label: string) => {
		setPayPeriodId(resolvedPayPeriodId);
		setPayPeriodLabel(label);
		const parsed = parsedRef.current;
		if (!parsed) return;
		try {
			const [fieldsRes, templatesRes] = await Promise.all([
				getApi(FIELDS_URL, { companyId, payPeriodId: resolvedPayPeriodId }),
				getApi(TEMPLATES_URL, { companyId }),
			]);
			const loadedSections: SectionDto[] = fieldsRes.data?.sections ?? [];
			const loadedTemplates: MappingTemplate[] = Array.isArray(templatesRes.data) ? templatesRes.data : [];
			setSections(loadedSections);
			setTemplates(loadedTemplates);

			// Category suggestion must run BEFORE negative-amount detection: every
			// non-name column starts out at category 'skip' (buildAutoDetectedMappings),
			// and detectNegativeDefaults deliberately leaves 'skip' columns alone (so it
			// never fights a real Skip choice) — negating before a column has its real
			// category would make detection a no-op for every cost column.
			let mappings = buildAutoDetectedMappings(parsed.headers, parsed.rawMatrix, parsed.headerRowIndex);
			mappings = suggestCategories(mappings, loadedSections);
			mappings = autoSelectSoleField(mappings, loadedSections);
			mappings = detectNegativeDefaults(parsed.rawMatrix, parsed.headerRowIndex, mappings);
			dispatch({ type: 'STEP2_INITIALIZED', columnMappings: mappings });

			const match = findBestTemplateMatch(parsed.headers, loadedTemplates);
			if (match) {
				setTemplateMatch(match);
				appendMessage({
					smartImportStep: 'template',
					text: `I found a saved template "${match.template.name}" that matches ${match.overlapCount} of your ${match.totalColumns} columns. Apply it?`,
				});
			} else {
				const negCount = mappings.filter((m) => m.negate).length;
				appendMessage({
					smartImportStep: 'mapping',
					text:
						`No saved template matched this file — here's my best guess at the mapping.` +
						(negCount > 0
							? ` I noticed negative amounts and turned on Convert for ${negCount} column(s).`
							: '') +
						` Review and continue when ready.`,
				});
			}
		} catch {
			appendMessage({ text: 'Could not load fields and templates for this pay period. Please try again.' });
		}
	};

	/** Entry point: user attached a CSV/XLSX and sent the message. */
	const startImport = async (file: File) => {
		if (!companyId) {
			appendMessage({ text: 'No company is selected — pick a company before importing.' });
			return;
		}
		if (activeRef.current) {
			freezeActiveMessage('Cancelled — a new import was started.');
		}
		dispatch({ type: 'RESET' });
		resetLocal();
		activeRef.current = true;

		let detected;
		try {
			detected = await loadFile(file);
		} catch (e) {
			appendMessage({
				text: `I couldn't read that file${e instanceof Error ? `: ${e.message}` : ''}. Try a CSV or XLSX export.`,
			});
			activeRef.current = false;
			return;
		}

		dispatch({ type: 'FILE_SELECTED', file });
		const payload = buildParsePayload(detected);
		dispatch({ type: 'PARSE_SUCCEEDED', payload });
		dispatch({ type: 'ADVANCE_STEP' }); // past "upload" — the file is already attached

		const headers = (payload.rawMatrix[payload.headerRowIndex] ?? []).map(String);
		parsedRef.current = { headers, rawMatrix: payload.rawMatrix, headerRowIndex: payload.headerRowIndex };

		try {
			const res = await getApi(PAY_PERIODS_URL, { companyId });
			const list: Array<{ id: string; startDate?: string; endDate?: string }> = res.data?.data ?? [];
			if (list.length === 0) {
				appendMessage({ text: 'This company has no pay periods set up yet — create one before importing.' });
				return;
			}
			const storedId = localStorage.getItem('payPeriod');
			const stored = storedId ? list.find((p) => p.id === storedId) : undefined;
			if (stored) {
				await afterPayPeriodResolved(stored.id, formatPeriodLabel(stored));
				return;
			}
			if (list.length === 1) {
				await afterPayPeriodResolved(list[0].id, formatPeriodLabel(list[0]));
				return;
			}
			const options: PayPeriodOption[] = list.map((p) => ({ id: p.id, label: formatPeriodLabel(p) || p.id }));
			setPayPeriodOptions(options);
			appendMessage({ smartImportStep: 'pay_period', text: 'Which pay period should this import into?' });
		} catch {
			appendMessage({ text: 'Could not load pay periods. Please try again.' });
		}
	};

	const selectPayPeriod = async (id: string) => {
		const label = payPeriodOptions.find((p) => p.id === id)?.label ?? '';
		freezeActiveMessage(`Importing into ${label || 'the selected pay period'}.`);
		await afterPayPeriodResolved(id, label);
	};

	// ---- Template step ---------------------------------------------------------
	const applyTemplate = () => {
		if (!templateMatch) return;
		const entries = parseTemplateEntries(templateMatch.template.mappings);
		const { mappings, unresolved, pendingFieldsToCreate } = resolveTemplateToMappings(
			session.columnMappings,
			entries,
			sections
		);
		dispatch({ type: 'TEMPLATE_APPLIED', columnMappings: mappings, loadedTemplateId: templateMatch.template.id });
		for (const pf of pendingFieldsToCreate) {
			dispatch({
				type: 'PENDING_FIELD_ADDED',
				colIndex: pf.colIndex,
				pendingField: { tempId: pf.tempId, label: pf.label, sectionId: pf.sectionId, derivedJsonId: pf.derivedJsonId },
			});
		}
		freezeActiveMessage(`Applied template "${templateMatch.template.name}".`);
		appendMessage({
			smartImportStep: 'mapping',
			text:
				`Template applied.` +
				(unresolved.length > 0
					? ` ${unresolved.length} column(s) couldn't be matched and were set to Skip — you may want to remap them.`
					: '') +
				` Review the mapping and continue when ready.`,
		});
	};

	const declineTemplate = () => {
		freezeActiveMessage('Skipped the saved template.');
		appendMessage({
			smartImportStep: 'mapping',
			text: "OK — here's my best guess at the mapping instead. Review and continue when ready.",
		});
	};

	// ---- Mapping step ------------------------------------------------------
	const mappingValidationError = getStep2ValidationError(session.columnMappings);

	const reSuggestCategories = () => {
		dispatch({ type: 'CATEGORIES_SUGGESTED', columnMappings: suggestCategories(session.columnMappings, sections) });
	};

	const saveMappingAsTemplate = async (name: string): Promise<boolean> => {
		try {
			await postApi(TEMPLATES_URL, {
				companyId,
				name,
				mappings: buildMappingsPayload(session.columnMappings, sections, session.pendingFields),
			});
			return true;
		} catch {
			return false;
		}
	};

	const continueFromMapping = async () => {
		if (mappingValidationError || !parsedRef.current) return;
		freezeActiveMessage('Mapping confirmed.');
		setLoading(true);
		try {
			const [rosterRes, aliasRes] = await Promise.all([
				postApi(EMPLOYEES_URL, { companyId, status: 'all' }, false, { limit: 10000, page: 1 }),
				getApi(ALIASES_URL, { companyId, mappingType: 'employee_name' }),
			]);
			const rosterResult: RosterEmployee[] = rosterRes.data?.data?.data ?? [];
			setRoster(rosterResult);

			const nameColConfig = buildNameColConfig(session);
			if (!nameColConfig) {
				appendMessage({ text: 'No Employee Name column is mapped — go back and map one to continue.' });
				setLoading(false);
				return;
			}

			const aliasMap = new Map<string, string>();
			for (const a of aliasRes.data?.aliases ?? []) {
				if (a.csvValue && a.employeeId) aliasMap.set(a.csvValue, a.employeeId);
			}

			const { headers, rawMatrix, headerRowIndex } = parsedRef.current;
			const dataRows = rawMatrix
				.slice(headerRowIndex + 1)
				.filter((r) => r.some((c) => String(c ?? '').trim() !== '') && !isTotalRow(r));
			const matrix = aggregateByEmployee(headers, dataRows, nameColConfig);
			const costMappings = session.columnMappings.filter(
				(m) => m.category !== 'employee_name' && m.category !== 'skip' && m.fieldRef.type !== 'none'
			);
			const aggregatedRows = buildAggregatedRows(matrix, nameColConfig, costMappings);
			const employeeRows = computeEmployeeRows(aggregatedRows, rosterResult, aliasMap);
			dispatch({ type: 'STEP3_INITIALIZED', payload: { aggregatedRows, employeeRows } });
			dispatch({ type: 'ADVANCE_STEP' });

			const reviewCount = employeeRows.filter((r) => r.matchStatus === 'review').length;
			const newCount = employeeRows.filter((r) => r.matchStatus === 'new').length;
			const blankCount = employeeRows.filter((r) => r.matchStatus === 'blank_name').length;
			const autoCount = employeeRows.length - reviewCount - newCount - blankCount;
			appendMessage({
				smartImportStep: 'employees',
				text:
					`Matched ${autoCount} of ${employeeRows.length} employees automatically.` +
					(reviewCount > 0 ? ` ${reviewCount} need your confirmation.` : '') +
					(newCount > 0 ? ` ${newCount} look new.` : '') +
					(blankCount > 0 ? ` ${blankCount} have a blank name and need attention.` : '') +
					` Review below.`,
			});
		} catch {
			appendMessage({ text: 'Could not load employees. Please try again.' });
		} finally {
			setLoading(false);
		}
	};

	// ---- Employees step ------------------------------------------------------
	const canAdvanceFromEmployees =
		session.employeeRows.some((r) => !r.skipped) &&
		session.employeeRows.every(
			(r) => r.skipped || (r.matchStatus !== 'review' && r.matchStatus !== 'blank_name' && !isBlankNewName(r))
		);

	const continueFromEmployees = async () => {
		if (!canAdvanceFromEmployees || !payPeriodId) return;
		freezeActiveMessage('Employee mapping confirmed.');
		setLoading(true);
		try {
			const aliases: Array<{ csvValue: string; employeeId: string }> = [];
			for (const r of session.employeeRows) {
				if (!r.skipped && (r.matchStatus === 'matched' || r.matchStatus === 'saved') && r.matchedEmployeeId) {
					aliases.push({ csvValue: r.csvName, employeeId: r.matchedEmployeeId });
				}
			}
			await postApi(ALIASES_URL, { companyId, mappingType: 'employee_name', source: 'import', aliases });
			dispatch({ type: 'ADVANCE_STEP' });

			const fieldsRes = await getApi(FIELDS_URL, { companyId, payPeriodId });
			const loadedSections: SectionDto[] = fieldsRes.data?.sections ?? [];
			const names: Record<string, string> = {};
			for (const s of loadedSections) {
				for (const f of s.fields) {
					if (!f.isTotal) names[f.id] = f.name;
				}
			}
			setFieldNames(names);
			appendMessage({
				smartImportStep: 'preview',
				text: "Here's a preview of what will be imported. Nothing is saved yet — confirm below when it looks right.",
			});
		} catch {
			appendMessage({ text: 'Failed to save employee mappings. Please try again.' });
		} finally {
			setLoading(false);
		}
	};

	// ---- Preview / Confirm step ------------------------------------------------
	const confirmImport = async () => {
		if (!payPeriodId) return;
		setSubmitting(true);
		setResult(null);
		try {
			const payload = buildConfirmPayload(session, { companyId, payPeriodId });
			const res = await postApi(CONFIRM_URL, payload);
			const apiResult = res.data as SmartImportResponse;
			const parts = [
				apiResult.createdEmployees > 0 && `${apiResult.createdEmployees} employee(s) added`,
				apiResult.reactivatedEmployees > 0 && `${apiResult.reactivatedEmployees} reactivated`,
				apiResult.createdFields > 0 && `${apiResult.createdFields} field(s) created`,
				`${apiResult.upsertedCosts} cost value(s) imported`,
			]
				.filter(Boolean)
				.join(', ');
			let text = `Import complete: ${parts}.`;
			if (apiResult.warnings.length > 0) {
				text += ` ${apiResult.warnings.length} warning(s): ${apiResult.warnings.map((w) => w.message).join('; ')}`;
			}
			freezeActiveMessage(`Import confirmed for ${payPeriodLabel || 'the selected pay period'}.`);
			setResult({ success: true, text, errors: [] });
			appendMessage({ smartImportStep: 'result', text });
			notifyImported(payPeriodId);
			activeRef.current = false;
		} catch (err: unknown) {
			const status = (err as { response?: { status?: number } })?.response?.status;
			if (status === 422) {
				const errors = parseConfirmErrors(err);
				const text = `${errors.length} issue${errors.length === 1 ? '' : 's'} found — fix the highlighted items and re-import.`;
				setResult({ success: false, text, errors });
				appendMessage({ smartImportStep: 'result', text });
			} else {
				const text = 'Import failed. Please try again.';
				setResult({ success: false, text, errors: [] });
				appendMessage({ smartImportStep: 'result', text });
			}
		} finally {
			setSubmitting(false);
		}
	};

	const fixStep = (step: 1 | 2) => {
		setResult(null);
		dispatch({ type: 'GOTO_STEP', step });
		freezeActiveMessage('Fixing the flagged issues…');
		appendMessage(
			step === 1
				? { smartImportStep: 'mapping', text: 'Back to column mapping — fix the highlighted issues and continue.' }
				: { smartImportStep: 'employees', text: 'Back to employee mapping — fix the highlighted issues and continue.' }
		);
	};

	const retryConfirm = () => {
		void confirmImport();
	};

	const cancelImport = () => {
		freezeActiveMessage('Import cancelled.');
		dispatch({ type: 'RESET' });
		resetLocal();
		activeRef.current = false;
	};

	return {
		session,
		dispatch,
		sections,
		templates,
		templateMatch,
		payPeriodOptions,
		payPeriodId,
		payPeriodLabel,
		roster,
		fieldNames,
		loading,
		submitting,
		result,
		mappingValidationError,
		canAdvanceFromEmployees,
		startImport,
		selectPayPeriod,
		applyTemplate,
		declineTemplate,
		reSuggestCategories,
		saveMappingAsTemplate,
		continueFromMapping,
		continueFromEmployees,
		confirmImport,
		retryConfirm,
		fixStep,
		cancelImport,
		getErrorLocus: (e: PreValidationError) => getErrorLocus(e, session.pendingFields, session.employeeRows),
		getErrorStep,
		isPendingFieldKey,
	};
}

export type SmartImportChat = ReturnType<typeof useSmartImportChat>;
